import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Tooltip, Switch, Select as AntSelect, Spin } from 'antd'
import { faAdd } from '@fortawesome/pro-solid-svg-icons/faAdd'
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash'
import { faGraduationCap } from '@fortawesome/pro-solid-svg-icons/faGraduationCap'
import { RankmiTable } from '@rankmi/rankmi-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown'
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp'
import { slices } from 'store/slices'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import EmptyState from 'components/reusables/emptyState'
import Select from 'components/reusables/select'
import { ILearningPath } from 'interfaces/learningPath'
import { LearningPathStatus } from 'enum/learningPathStatus.enum'
import { learningPathsService } from 'api/administration/learningPaths'
import {
  SwitchWrapper,
  SwitchTitle,
  SwitchSubtitle,
  Container,
  Header,
  Title,
  Content,
  Options,
  AddLearningPaths,
} from './styled'
import Details from './details'

const { Option } = AntSelect

const Prerequisites = () => {
  const { t } = useTranslation()
  const prefix =
    'administration.learningPaths.manage.create.content.prerequisites'
  const dispatch = useAppDispatch()
  const learningPaths = useAppSelector(slices.learningPath.items)
  const loadingLearningPaths = useAppSelector(slices.learningPath.loading)
  const loadedLearningPaths = useAppSelector(slices.learningPath.loaded)
  const content = useAppSelector(slices.learningPath.content)
  const configuration = useAppSelector(slices.learningPath.configuration)
  const [isOpen, setIsOpen] = useState(true)
  const [addPrerequisites, setAddPrerequisites] = useState(false)
  const [hasEmptyLearningPath, setHasEmptyLearningPath] = useState(false)
  const [isSeeingDetail, setIsSeeingDetail] = useState(false)
  const [selectedLearningPath, setSelectedLearningPath] =
    useState<ILearningPath | null>(null)

  useEffect(() => {
    if (!loadedLearningPaths && !loadingLearningPaths) getLearningPaths()
  }, [])

  useEffect(() => {
    setHasEmptyLearningPath(false)
    content.learningPaths.forEach((item) => {
      if (item === 0) {
        setHasEmptyLearningPath(true)
      }
    })
    setAddPrerequisites(
      content.learningPaths.filter(
        item => item > 0
      ).length > 0
    )
  }, [content.learningPaths])

  const getLearningPaths = async () => {
    dispatch(slices.learningPath.setLoading(true))
    await learningPathsService.getLearningPaths().then((response) => {
      dispatch(slices.learningPath.setItems(response))
    })
    dispatch(slices.learningPath.setLoading(false))
  }

  const onSwitchChange = (value: boolean) => {
    setAddPrerequisites(value)
    if (!value) dispatch(slices.learningPath.removeAllLearningPath())
  }

  const seeDetail = (value: number) => {
    learningPaths.forEach((item) => {
      if (item.id === value) {
        setSelectedLearningPath(item)
        setIsSeeingDetail(true)
      }
    })
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCount`),
      rowsCount_plural: t(`${prefix}.rowsCountPlural`),
    },
  }

  const columns = [
    {
      title: t(`${prefix}.columns.selectLearningPath.title`),
      render: (id: number) => (
        <Spin spinning={loadingLearningPaths}>
          <Select
            showSearch={true}
            value={id || undefined}
            placeholder={t(`${prefix}.columns.selectLearningPath.placeholder`)}
            onSelect={(value) =>
              dispatch(
                slices.learningPath.changeLearningPath({
                  learningPath: value,
                  previousLearningPath: id,
                })
              )
            }
            notFoundContent={
              <EmptyState
                title={t(`${prefix}.columns.selectLearningPath.emptyState`)}
                icon={faGraduationCap}
              />
            }
          >
            {learningPaths
              .filter((item) => {
                const selected = item.id === id
                const published = item.state === LearningPathStatus.published
                const noSameLearningPath = item.id !== configuration.id
                return (
                  selected ||
                  (published &&
                    noSameLearningPath &&
                    !content.learningPaths.includes(item.id))
                )
              })
              .map((item) => (
                <Option key={item.id} value={item.id} label={item.name}>
                  {item.name}
                </Option>
              ))}
          </Select>
        </Spin>
      ),
    },
    {
      width: 160,
      render: (id: number) => (
        <Options>
          <Button onClick={() => seeDetail(id)} disabled={id === 0}>
            {t(`${prefix}.columns.options.seeDetails`)}
          </Button>
          <Tooltip
            title={t(`${prefix}.columns.options.remove`)}
            placement="right"
          >
            <Button
              type="link"
              onClick={() =>
                dispatch(slices.learningPath.removeLearningPath(id))
              }
              disabled={id === 0}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Tooltip>
        </Options>
      ),
    },
  ]

  return (
    <>
      <SwitchWrapper>
        <Switch
          checked={addPrerequisites}
          onChange={() => onSwitchChange(!addPrerequisites)}
        />
        <SwitchTitle>{t(`${prefix}.title`)}</SwitchTitle>
        <SwitchSubtitle>{t(`${prefix}.description`)}</SwitchSubtitle>
      </SwitchWrapper>
      {addPrerequisites && (
        <Container>
          <Header>
            <Title>{t(`${prefix}.header`)}</Title>
            <Button type="link" onClick={() => setIsOpen(!isOpen)}>
              <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
            </Button>
          </Header>
          <Content open={isOpen}>
            <RankmiTable
              locale={locale}
              columnHeaders={columns}
              data={content.learningPaths}
              total={
                hasEmptyLearningPath
                  ? content.learningPaths.length - 1
                  : content.learningPaths.length
              }
              countIcon={faGraduationCap}
            />
            <AddLearningPaths>
              <Button
                type="link"
                onClick={() =>
                  dispatch(slices.learningPath.addEmptyLearningPath())
                }
                disabled={hasEmptyLearningPath}
              >
                <FontAwesomeIcon icon={faAdd} />
                {t(`${prefix}.buttons.addLearningPaths`)}
              </Button>
            </AddLearningPaths>
            {isSeeingDetail && selectedLearningPath && (
              <Details
                id={selectedLearningPath.id}
                name={selectedLearningPath.name}
                close={() => setIsSeeingDetail(false)}
              />
            )}
          </Content>
        </Container>
      )}
    </>
  )
}

export default Prerequisites
